import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Play, Pause as PauseIcon, Pencil, Trash2, LineChart } from "lucide-react";
import { api, fmtTime } from "@/lib/api";
import { SectionTitle, Badge } from "@/components/Bits";

const SIGNAL_LABEL = {
  rsi_divergence: "RSI Div",
  rsi_absolute: "RSI Abs",
  cvd_divergence: "CVD Div",
  liquidation_filter: "Liq Filter",
};

function ConditionsSummary({ p }) {
  const groups = Array.isArray(p.condition_groups) ? p.condition_groups : [];
  if (groups.length > 0) {
    // Grouped mode summary: `(1m Setup A: A+B) OR (5m Setup B: C)`.
    // Per-group timeframe surfaces in the prefix when set; falls back
    // to the profile's tf otherwise. Reads from condition_groups
    // directly; ignores the flat `conditions` field (operator opted in).
    return (
      <div className="flex flex-wrap items-center gap-1">
        {groups.map((g, gi) => {
          const cs = g.conditions || [];
          const gtf = g.timeframe || p.timeframe;
          return (
            <span key={g.id || gi} className="flex items-center gap-1">
              <span className="text-[#71717A]">(</span>
              <span className="font-mono text-[10px] text-[#3B82F6]">{gtf}</span>
              {g.name ? (
                <span className="text-[#A0A0AB] text-[10px]">{g.name}:</span>
              ) : null}
              {cs.length === 0 ? (
                <span className="text-[#71717A] italic">empty</span>
              ) : cs.map((c, i) => (
                <span key={c.id || i} className="flex items-center gap-1">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.enabled ? "bg-[#00E676]" : "bg-[#71717A]"}`} />
                  <span>{SIGNAL_LABEL[c.type] || c.type}</span>
                  {i < cs.length - 1 ? <span className="text-[#71717A] px-1">AND</span> : null}
                </span>
              ))}
              <span className="text-[#71717A]">)</span>
              {gi < groups.length - 1 ? (
                <span className="text-[#71717A] px-1">OR</span>
              ) : null}
            </span>
          );
        })}
      </div>
    );
  }
  const conds = p.conditions || [];
  if (conds.length === 0) return <span className="text-[#71717A]">no conditions</span>;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {conds.map((c, i) => (
        <span key={c.id || i} className="flex items-center gap-1">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.enabled ? "bg-[#00E676]" : "bg-[#71717A]"}`} />
          <span>{SIGNAL_LABEL[c.type] || c.type}</span>
          {c.direction_filter && c.direction_filter !== "any" ? (
            <span className="text-[#71717A] text-[10px]">({c.direction_filter})</span>
          ) : null}
          {i < conds.length - 1 ? (
            <span className="text-[#71717A] px-1">{p.combinator}</span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

export default function StrategyProfiles() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [missingActiveId, setMissingActiveId] = useState(null);

  const load = async () => {
    setLoadError(null);
    // Resolve each call independently: a transient failure on `active` or
    // `phase` must NOT prevent the profiles list from rendering. The list
    // endpoint is the only one whose failure is fatal for this page.
    const [listRes, activeRes, phaseRes] = await Promise.allSettled([
      api.strategyProfiles(),
      api.strategyActive(),
      api.validationPhaseStatus(),
    ]);

    if (listRes.status === "rejected") {
      const e = listRes.reason;
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      const msg = status ? `HTTP ${status}: ${detail}` : detail;
      console.error("[strategies] load failed", msg);
      setLoadError(msg);
      toast.error(`Failed to load Strategy Profiles — ${msg}`);
      setLoading(false);
      return;
    }
    setProfiles(listRes.value || []);

    if (activeRes.status === "fulfilled") {
      setActiveId(activeRes.value?.active_strategy_id || null);
    } else {
      console.warn("[strategies] active state load failed (non-fatal):", activeRes.reason);
      setActiveId(null);
    }

    if (phaseRes.status === "fulfilled") {
      setMissingActiveId(phaseRes.value?.active_strategy_missing ? phaseRes.value?.active_strategy_missing_id : null);
    } else {
      setMissingActiveId(null);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    try {
      const stub = {
        name: `New Strategy ${new Date().toISOString().slice(11, 19)}`,
        description: "",
        timeframe: "5m",
        combinator: "AND",
        conditions: [],
      };
      const created = await api.strategyCreate(stub);
      toast.success("Profile created — add conditions");
      navigate(`/entry-rules?profile_id=${encodeURIComponent(created.id)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const onEdit = (p) => {
    navigate(`/entry-rules?profile_id=${encodeURIComponent(p.id)}`);
  };

  const onActivate = async (p) => {
    try {
      setBusyId(p.id);
      await api.strategyActivate(p.id);
      toast.success(`Activated: ${p.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusyId(null); }
  };

  const onDeactivate = async () => {
    try {
      setBusyId("__any__");
      await api.strategyDeactivate();
      toast.success("No active strategy");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusyId(null); }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete profile "${p.name}"?`)) return;
    try {
      setBusyId(p.id);
      await api.strategyDelete(p.id);
      toast.success("Profile deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusyId(null); }
  };

  if (loading) {
    return <div className="p-6 text-sm text-[#A0A0AB]">Loading…</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <SectionTitle
        sub="Overview of all strategies. Exactly one can be ACTIVE at a time for automated testnet execution. Click Edit on a profile to tune its parameters. Per-side open-position caps are enforced from Risk Settings."
        right={
          <Button
            data-testid="create-profile-btn"
            onClick={onCreate}
            className="rounded-sm bg-[#00E676] hover:bg-[#00C766] text-black font-semibold"
          >
            <Plus size={14} strokeWidth={2} className="mr-1" /> New Profile
          </Button>
        }
      >
        Strategy Profiles
      </SectionTitle>

      {activeId ? (
        <div data-testid="active-strategy-bar" className="bg-[#00E676]/10 border border-[#00E676]/40 rounded-sm px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E676] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00E676]" />
            </span>
            <div className="text-sm">
              <span className="text-[#A0A0AB] uppercase tracking-wider text-[10px] mr-2">Active strategy</span>
              <span className="font-semibold text-[#00E676]">
                {profiles.find((p) => p.id === activeId)?.name || "—"}
              </span>
            </div>
          </div>
          <Button
            data-testid="deactivate-strategy-btn"
            variant="outline"
            size="sm"
            disabled={busyId !== null}
            onClick={onDeactivate}
            className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
          >
            <PauseIcon size={12} strokeWidth={2} className="mr-1" /> Deactivate
          </Button>
        </div>
      ) : (
        <div className="bg-[#1A1A22] border border-border rounded-sm px-4 py-3 text-xs text-[#A0A0AB]">
          <span className="uppercase tracking-wider text-[10px] text-[#71717A] mr-2">No active strategy</span>
          The validation engine falls back to the built-in hardcoded RSI divergence harness until you activate a profile.
        </div>
      )}

      {missingActiveId ? (
        <div data-testid="active-strategy-missing-banner" className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-sm px-4 py-3 text-xs text-[#FF3B30] flex items-start gap-2">
          <span className="font-semibold uppercase tracking-wider text-[10px] mr-2 mt-0.5">⚠ Active strategy missing</span>
          <span className="normal-case tracking-normal">
            The previously active strategy <code className="font-mono">{missingActiveId.slice(0, 8)}…</code> was deleted. Engine is no longer evaluating any profile — please activate a profile below.
          </span>
        </div>
      ) : null}

      {loadError ? (
        <div data-testid="profiles-load-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-sm px-4 py-3 text-xs text-[#FF3B30] flex items-start gap-2">
          <span className="font-semibold uppercase tracking-wider text-[10px] mr-2 mt-0.5">Load error</span>
          <span className="normal-case tracking-normal flex-1">
            {loadError} — please refresh. If this persists, your session may be expired or the backend is unreachable.
          </span>
          <Button size="sm" variant="outline" onClick={load} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs h-7">
            Retry
          </Button>
        </div>
      ) : null}

      <div data-testid="profiles-grid" className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {profiles.map((p) => {
          const isActive = p.id === activeId;
          return (
            <div
              key={p.id}
              data-testid={`profile-card-${p.id}`}
              className={`bg-[#0F0F14] border rounded-sm p-4 flex flex-col gap-3 transition-colors ${
                isActive ? "border-[#00E676]/50 bg-[#00E676]/5" : "border-border"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{p.name}</span>
                    {isActive ? (
                      <Badge kind="on" testid={`badge-active-${p.id}`}>ACTIVE</Badge>
                    ) : (
                      <Badge kind="off" testid={`badge-inactive-${p.id}`}>INACTIVE</Badge>
                    )}
                  </div>
                  <div className="text-xs text-[#A0A0AB] mt-1 truncate" title={p.description}>
                    {p.description || "—"}
                  </div>
                </div>
                <Badge kind="neutral">
                  {Array.isArray(p.condition_groups) && p.condition_groups.length > 0
                    ? `${p.condition_groups.length} groups · ${(p.condition_groups || []).reduce((n, g) => n + (g.conditions || []).length, 0)} cond`
                    : `${p.combinator || "AND"} · ${(p.conditions || []).length} cond`}
                </Badge>
              </div>

              <div className="grid grid-cols-4 gap-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#71717A]">TF</div>
                  <div className="font-mono">{p.timeframe}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#71717A]">Cooldown</div>
                  <div className="font-mono">{p.cooldown_minutes}m</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#71717A]">Long</div>
                  <div className="font-mono">{p.allow_long ? "✓" : "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#71717A]">Short</div>
                  <div className="font-mono">{p.allow_short ? "✓" : "—"}</div>
                </div>
              </div>

              <div className="text-[11px] text-[#A0A0AB] font-mono border-t border-border/40 pt-2">
                <ConditionsSummary p={p} />
              </div>

              <div className="text-[10px] text-[#71717A] font-mono">
                exit: {p.exit_rules?.mode || "tp_sl"}
                {p.exit_rules?.mode !== "manual" ? (
                  <>
                    {p.exit_rules?.take_profit_enabled ? ` · TP ${p.exit_rules?.take_profit_percent}%` : ""}
                    {p.exit_rules?.stop_loss_enabled ? ` · SL ${p.exit_rules?.stop_loss_percent}%` : ""}
                  </>
                ) : null}
                {" · "}created {fmtTime(p.created_at)}
              </div>

              <div className="flex items-center justify-end gap-2 mt-1">
                {isActive ? (
                  <Button
                    data-testid={`profile-deactivate-${p.id}`}
                    size="sm"
                    variant="outline"
                    onClick={onDeactivate}
                    disabled={busyId !== null}
                    className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
                  >
                    <PauseIcon size={12} strokeWidth={2} className="mr-1" /> Deactivate
                  </Button>
                ) : (
                  <Button
                    data-testid={`profile-activate-${p.id}`}
                    size="sm"
                    onClick={() => onActivate(p)}
                    disabled={busyId !== null}
                    className="rounded-sm bg-[#00E676] hover:bg-[#00C766] text-black font-semibold text-xs"
                  >
                    <Play size={12} strokeWidth={2} className="mr-1" /> Activate
                  </Button>
                )}
                <Button
                  data-testid={`profile-edit-${p.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => onEdit(p)}
                  className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
                >
                  <Pencil size={12} strokeWidth={2} className="mr-1" /> Edit
                </Button>
                <Button
                  data-testid={`profile-backtest-${p.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/backtest?strategy_id=${p.id}`)}
                  className="rounded-sm border-[#3B82F6]/40 bg-[#1A1A22] hover:bg-[#3B82F6]/10 text-[#3B82F6] text-xs"
                  title="Open Backtest with this strategy preloaded"
                >
                  <LineChart size={12} strokeWidth={2} className="mr-1" /> Backtest
                </Button>
                <Button
                  data-testid={`profile-delete-${p.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => onDelete(p)}
                  disabled={busyId === p.id}
                  className="rounded-sm border-[#FF3B30]/40 bg-[#1A1A22] hover:bg-[#FF3B30]/10 text-[#FF3B30] text-xs"
                >
                  <Trash2 size={12} strokeWidth={2} />
                </Button>
              </div>
            </div>
          );
        })}
        {profiles.length === 0 ? (
          <div className="col-span-full bg-[#0F0F14] border border-border rounded-sm p-8 text-sm text-[#71717A] text-center">
            No profiles yet. Click <span className="text-white">New Profile</span> to create one.
          </div>
        ) : null}
      </div>
    </div>
  );
}
